import { useEffect, useState, useMemo, useRef } from 'react'
import { useTranslation } from "react-i18next";
import { ReactComponent as IconChevronExpand } from 'bootstrap-icons/icons/chevron-expand.svg'
import { ReactComponent as IconTrash } from 'bootstrap-icons/icons/trash.svg'
import { ReactComponent as IconArrowClockwise } from 'bootstrap-icons/icons/arrow-clockwise.svg'
import { ReactComponent as IconInfoCircle } from 'bootstrap-icons/icons/info-circle.svg'
import { ReactComponent as IconJournalText } from 'bootstrap-icons/icons/journal-text.svg'
import debounce from 'lodash.debounce'

import getAccessToken from "hooks/getAccessToken";
import parseDate from 'lib/parseDate'
import useMutation from "hooks/useMutation";
import Status from './Status'
import Calendar from './Calendar'
import sanitize from '../../../EHR/V2/sanitize'

const App = props => {
  const { t } = useTranslation()
  const [state, setState] = useState({ ...props.state })
  const [loading, setLoading] = useState()
  const [mutate, mutation] = useMutation(window._l(`/records`).substr(1));
  const $collapse = useRef(null)
  /* WORKAROUND ya que cuando creas un nuevo registro hace el POST sin el access token */
  const token = getAccessToken()
  /* onChange llega distinto en cada render del padre (Treatments.js no lo
   * memoiza), pero el debounce de abajo se crea UNA sola vez por instancia
   * (deps [token.value]). Sin este ref, `save` quedaba con el props.onChange
   * de la primera vez que se montó esta fila, y al disparar 2s después
   * pisaba el array completo del store con un snapshot viejo (borrando de
   * la pantalla cualquier tratamiento agregado después de este). */
  const onChangeRef = useRef(props.onChange)
  useEffect(() => {
    onChangeRef.current = props.onChange
  }, [props.onChange])

  const onChange = payload => {
    if (!state) return
    const record = { ...state, ...payload }
    if (payload.status && payload.status === 'done' && (!record.endsAt || parseDate(record.endsAt).getTime() >= Date.now())) {
      record.endsAt = new Date().toLocaleDateString('fr-ca')
    } else if (payload.status && payload.status !== 'done' && record.endsAt && parseDate(record.endsAt).toLocaleDateString('fr-ca') <= new Date().toLocaleDateString('fr-ca')) {
      record.endsAt = null
    }
    setState(record)
    setLoading(true)
    debouncedChangeHandler(record)
  }

  const save = record => {
    // Bug real (auditoría 2026-08-16): setLoading(false) se llamaba ANTES de
    // que mutate() siquiera arrancara el request (no en un callback), así
    // que el spinner de guardado desaparecía instantáneamente sin importar
    // si el POST/PUT seguía en curso o fallaba. Encima nunca se pasaba
    // onError a mutate(): un fallo de red/servidor quedaba completamente en
    // silencio para el medico (ni loading, ni error, el registro parecia
    // guardado bien). Ahora loading se apaga recien cuando la mutación
    // termina (éxito o error), y un error muestra un toast igual que el
    // resto de la app.
    mutate(record, {
      onSuccess: data => {
        setLoading(false)
        onChangeRef.current({ ...data, ...record })
      },
      onError: error => {
        setLoading(false)
        window.notify({ message: error.message, type: 'danger', title: 'Error' })
      }
    })
  }

  const debouncedChangeHandler = useMemo(
    () => debounce(save, 2000)
    , [token.value]
  );

  const toggle = () => {
    const deleted = !(state.deleted ?? false)
    if (state.id && deleted && !window.confirm(t('ehr:remove_record', 'Confirmas que queres borrar el registro', { context: state.type }))) {
      return
    }
    onChange({ deleted })
  }

  useEffect(() => {
    setState({ ...props.state })
  }, [props.state])

  useEffect(() => {
    setState({ ...props.state })
    if (!props.state?.id && token.value) {
      setLoading(true)
      save(props.state)
    }
  }, [props.state, token.value])

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    }
  }, []);

  const options = [
    {
      value: 'active',
      label: t('active', 'En curso', { context: 'treatments' }),
      description: t('active_description_treatments', 'Trataminento en curso'),
      color: 'primary'
    },
    {
      value: 'pending',
      label: t('pending', 'Sin iniciar', { context: 'treatments' }),
      description: t('pending_description_treatments', 'El tratamiento no todavia no fue iniciado'),
      color: 'warning'
    },
    {
      value: 'done',
      label: t('done', 'Finalizado', { context: 'treatments' }),
      description: t('done_description_treatments', 'El tratamiento fue finalizado'),
      color: 'success'
    },
  ]

  const label = state.label || [state.drug, state.product, state.presentation].filter(Boolean).join(' - ')
  let tip = `<dt>${t('status', 'Estado')}</dt><dd>${t(state.status || 'active')}</dd>`
  // Bug real (auditoría 2026-08-16): notes/frecuency/dose (texto libre que
  // el médico tipea en los inputs de este mismo formulario) se concatenaban
  // crudos en este string HTML, que llega a data-tooltip-html —
  // react-tooltip renderiza ese string con dangerouslySetInnerHTML
  // internamente, así que era un sink de XSS indirecto (mismo mecanismo ya
  // corregido en otros lugares de la app, acá vía tres campos de
  // tratamientos).
  if (state.notes) {
    tip += `<dt>${t('notes', 'Notas')}</dt><dd>${sanitize(state.notes)}</dd>`
  }
  if (state.frecuency) {
    tip += `<dt>${t('frecuency', 'Frecuencia')}</dt><dd>${sanitize(state.frecuency)}</dd>`
  }
  if (state.dose) {
    tip += `<dt>${t('dose', 'Dosis')}</dt><dd>${sanitize(state.dose)}</dd>`
  }
  if (state.startsAt) {
    tip += `<dt>${t('startsAt', 'Inicio')}</dt><dd>${parseDate(state.startsAt).toLocaleDateString()}</dd>`
  }
  if (state.endsAt) {
    tip += `<dt>${t('endsAt', 'Fin')}</dt><dd>${parseDate(state.endsAt).toLocaleDateString()}</dd>`
  }
  if (state.date || state.createdAt) {
    tip += `<dt>${t('date', 'Cargado')}</dt><dd>${parseDate(state.date || state.createdAt).toLocaleDateString()}</dd>`
  }
  tip = `<dl>${tip}</dl>`

  return (
    <div className="position-relative border-top">
      {loading && <div className="loading loading-top loading-sm" />}
      <div className="d-flex justify-content-between align-items-center gap-2 p-3 bg-white">
        {/* Bug real (auditoría 2026-08-16): el id de collapse usaba solo
            state.id, undefined para un registro nuevo todavia no guardado
            ("collapseundefined" literal) — dos tratamientos nuevos en la
            misma pantalla colisionaban en el mismo id y rompian el
            collapse/expand de uno de los dos. state._id (uuid generado por
            Treatments.js al crear el registro) esta siempre presente incluso
            antes del POST, asi que sirve de fallback estable. */}
        <button className={`btn d-flex align-items-center gap-2 p-0 ${state.deleted ? 'link-danger' : 'link-dark'}`} data-bs-target={`#collapse${(state.id || state._id)?.replace('/', '-')}`} data-bs-toggle="collapse">
          {loading && (
            <span className="spinner-border spinner-border-sm text-primary me-1" />
          )}
          <IconChevronExpand width="14" height="14" className="link-primary" />
          {tip && (
            <IconInfoCircle width="14" height="14" className="text-teal" data-tooltip-id="app-tooltip" data-tooltip-html={tip} />
          )}
          {state.notes && (
            <IconJournalText width="14" height="14" className="text-teal" data-tooltip-id="app-tooltip" data-tooltip-content={state.notes}/>
          )}
          {label}
        </button>
        <div className="text-secondary small">
          {t(state.status || 'active', { context: 'treatments' })}
        </div>

      </div>
      <div className="collapse" ref={$collapse} id={`collapse${(state.id || state._id)?.replace('/', '-')}`}>
        <div className="p-3 d-flex flex-column border-top gap-3 shadow-sm bg-light mb-4 rounded-bottom">
          <div className="d-flex flex-column flex-xxl-row gap-2 align-items-xxl-center">
            <div className="flex-fill form-floating">
              <input
                disabled={loading && !state.id}
                value={state.notes || ''}
                onChange={e => onChange({ notes: e.target.value })}
                type="text"
                className="form-control focus-bg-light"
                // Bug real (auditoría 2026-08-16): id="notes"/"frecuency"/
                // "dose" fijos se repetian una vez por fila renderizada
                // (varios tratamientos en la lista) — ids duplicados en el
                // DOM. Se sufijan con el mismo identificador estable usado
                // para el collapse (id real o _id local).
                id={`notes-${state.id || state._id}`}
                name="notes"
                placeholder={t("notes_placeholder_short", "Notas")}
              />
              <label>{t("notes_placeholder_short", "Notas")}</label>
            </div>
            <div className="flex-shrink-1 form-floating">
              <input
                disabled={loading && !state.id}
                value={state.frecuency || ''}
                onChange={e => onChange({ frecuency: e.target.value })}
                type="text"
                className="form-control focus-bg-light"
                id={`frecuency-${state.id || state._id}`}
                name="frecuency"
                placeholder={t("frecuency_placeholder", "Frecuencia")}
              />
              <label>{t("frecuency_placeholder", "Frecuencia")}</label>
            </div>
            <div className="flex-shrink-1 form-floating">
              <input
                disabled={loading && !state.id}
                value={state.dose || ''}
                onChange={e => onChange({ dose: e.target.value })}
                type="text"
                className="form-control focus-bg-light"
                id={`dose-${state.id || state._id}`}
                name="dose"
                placeholder={t("dose_placeholder", "Dosis")}
              />
              <label>{t("dose_placeholder", "Dosis")}</label>
            </div>

            {/* Bug real (auditoría 2026-08-24): 4 dropdown toggles (Status +
                3 Calendar) en una fila sin flex-wrap — cada toggle muestra
                label+valor con text-truncate, que no acota nada sin un ancho
                fijo, así que en mobile el total desbordaba la fila en vez de
                truncarse o pasar a una segunda línea. */}
            <div className="d-flex justify-content-between flex-wrap gap-2 flex-fill">
              <Status
                disabled={loading && !state.id}
                label={t('status', 'Estado')}
                value={{
                  value: state.status ,
                  label: t(state.status || 'active')
                }}
                onChange={status => onChange({ status })}
                options={options}
              />

              <Calendar
                disabled={loading && !state.id}
                label={t('date', 'Fecha')}
                tip={t('treatment_date_tip', 'Fecha de carga')}
                value={{
                  value: state.date || state.createdAt,
                  label: parseDate(state.date || state.createdAt)?.toLocaleDateString()
                }}
                onChange={date => onChange({ date: date?.toLocaleDateString('fr-ca') || "" })}
              />

              <Calendar
                disabled={loading && !state.id}
                label={t('startsAt', 'inicio')}
                tip={t('treatment_startsAt_tip', 'Fecha de inicio del tratamiento')}
                value={{
                  value: state.startsAt,
                  label: parseDate(state.startsAt)?.toLocaleDateString() || t('undefined', 'Indefinida', { context: 'female' })
                }}
                onChange={date => onChange({ startsAt: date?.toLocaleDateString('fr-ca') || "" })}
              />

              <Calendar
                disabled={loading && !state.id}
                label={t('endsAt', 'inicio')}
                tip={t('treatment_endsAt_tip', 'Fecha de finalizacion del tratamiento')}
                value={{
                  value: state.endsAt,
                  label: parseDate(state.endsAt)?.toLocaleDateString() || t('undefined', 'Indefinido', { context: 'male' })
                }}
                onChange={date => onChange({ endsAt: date?.toLocaleDateString('fr-ca') || "" })}
              />
            </div>
            <div>
              <button
                disabled={loading && !state.id}
                data-tooltip-id="app-tooltip"
                data-tooltip-content={state.deleted ? t('restore', 'Restaurar') : undefined}
                className={state.deleted ? 'btn link-success' : 'btn link-danger'}
                onClick={toggle}
              >
                {state.deleted ? (
                  <>
                    <IconArrowClockwise className="opacity-75" width="16" height="16" />
                  </>
                ) : (
                  <>
                    <IconTrash className="opacity-75" width="16" height="16" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default App
